/**
 * Press Index Page
 * Landing page with links to press materials
 */

import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';
import { ROUTES } from '@routes/route-paths';
import { trackCtaClick } from '@logging';
import { BLUEPRINT_APRIL_2026, PRESEED_OCT_2026, DTEMPLATE } from '@components/press/pitch-deck';

/* Newest first. Each card's words come from its edition — the same title
   and date its password gate shows — so the press kit and the deck it
   opens cannot name one deck two ways. `cta` is the stable id the funnel
   groups by. */
const DECKS = [
  { edition: DTEMPLATE, to: ROUTES.PRESS_DTEMPLATE, cta: 'press_deck_dtemplate' },
  { edition: PRESEED_OCT_2026, to: ROUTES.PRESS_PRESEED_OCT_2026, cta: 'press_deck_preseed' },
  { edition: BLUEPRINT_APRIL_2026, to: ROUTES.PRESS_BLUEPRINT_APRIL_2026, cta: 'press_deck_blueprint' },
] as const;

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, ${colors.highlight.extraDark} 0%, #0F172A 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${spacing['2xl']};
`;

const PageTitle = styled.h1`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.semibold};
  color: white;
  margin: 0 0 ${spacing.sm};
`;

const PageSubtitle = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 ${spacing['3xl']};
`;

const DeckList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  width: 100%;
  max-width: 400px;
`;

const DeckCard = styled(Link)`
  display: block;
  padding: ${spacing.xl} ${spacing['2xl']};
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: ${borderRadius.xl};
  text-decoration: none;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  max-width: 400px;
  width: 100%;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(216, 243, 237, 0.2);
    transform: translateY(-2px);
  }
`;

const DeckCardTitle = styled.div`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: white;
  margin-bottom: ${spacing.xs};
`;

const DeckCardMeta = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  color: ${colors.brand.primary};
`;

export const PressIndexPage: React.FC = () => (
  <PageContainer>
    <PageTitle>Press Kit</PageTitle>
    <PageSubtitle>The Home CEO</PageSubtitle>
    {/* The destination page view records the arrival, so this event is not
        about traffic — it separates people who came to the press kit and
        chose the deck from people who were sent the deck link directly.
        Those are different audiences and the funnel reads differently for
        each. */}
    <DeckList>
      {DECKS.map(({ edition, to, cta }) => (
        <DeckCard key={to} to={to} onClick={() => trackCtaClick(cta, 'press_index')}>
          <DeckCardTitle>{edition.meta.subtitle}</DeckCardTitle>
          <DeckCardMeta>{edition.meta.date} &middot; Password Protected</DeckCardMeta>
        </DeckCard>
      ))}
    </DeckList>
  </PageContainer>
);
